import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import { PageHeader, Picker, Skeleton, ErrorState, useToast, useConfirm, useUnsavedChanges, DISCARD } from '../../../components/ui';
import { INCIDENT_TYPE_LABEL, SEVERITY_LABEL, INCIDENTS_PATH } from './AdminIncidents';
import '../booking-shared.css';
import './Safeguarding.css';

const EMPTY_FORM = {
  gymnastId: '',
  incidentDate: '',
  location: '',
  incidentType: 'INJURY',
  severity: 'MINOR',
  description: '',
  injuryDetails: '',
  firstAidGiven: '',
  outcome: '',
  witnessName: '',
  witnessContact: '',
  witnessStatement: '',
};

const Optional = () => <span className="bk-optional"> (optional)</span>;

// The people to choose from: each gymnast, with a parent's name to tell two
// gymnasts with the same name apart
export function gymnastItems(gymnasts) {
  return gymnasts.map(g => {
    const parents = (g.guardians || []).map(p => `${p.firstName} ${p.lastName}`);
    return {
      id: g.id,
      label: `${g.firstName} ${g.lastName}`,
      detail: parents.length ? `Parent: ${parents.join(', ')}` : undefined,
      search: (g.guardians || []).map(p => `${p.firstName} ${p.lastName} ${p.email || ''}`).join(' '),
    };
  });
}

function IncidentFormFields({ form, setForm, gymnasts, errors, gymnastLocked }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const items = useMemo(() => gymnastItems(gymnasts), [gymnasts]);
  return (
    <>
      <div className="bk-label">
        <label htmlFor="incident-gymnast">Gymnast<Optional /></label>
        <Picker
          id="incident-gymnast"
          label="Gymnast"
          items={items}
          value={form.gymnastId || ''}
          onChange={id => set('gymnastId', id)}
          placeholder="Search by name or parent…"
          disabled={gymnastLocked}
        />
        {gymnastLocked && <span className="sg-hint">The gymnast can't be changed once the report is filed.</span>}
      </div>
      <div className="bk-grid-2">
        <label className="bk-label">
          Date and time
          <input
            type="datetime-local"
            className="bk-input"
            value={form.incidentDate ? form.incidentDate.slice(0, 16) : ''}
            onChange={e => set('incidentDate', e.target.value)}
            aria-invalid={errors.incidentDate ? 'true' : undefined}
          />
          {errors.incidentDate && <span className="bk-field-error">{errors.incidentDate}</span>}
        </label>
        <label className="bk-label">
          Location<Optional />
          <input type="text" className="bk-input" value={form.location || ''} onChange={e => set('location', e.target.value)}
            placeholder="For example, main gym floor" />
        </label>
        <label className="bk-label">
          Type
          <select className="bk-input" value={form.incidentType} onChange={e => set('incidentType', e.target.value)}>
            {Object.entries(INCIDENT_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="bk-label">
          Severity
          <select className="bk-input" value={form.severity} onChange={e => set('severity', e.target.value)}>
            {Object.entries(SEVERITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <label className="bk-label">
        What happened
        <span className="sg-hint">In as much detail as you can.</span>
        <textarea className="bk-input" rows={4} value={form.description} onChange={e => set('description', e.target.value)}
          aria-invalid={errors.description ? 'true' : undefined} />
        {errors.description && <span className="bk-field-error">{errors.description}</span>}
      </label>
      <label className="bk-label">
        Injury or symptoms<Optional />
        <textarea className="bk-input" rows={2} value={form.injuryDetails || ''} onChange={e => set('injuryDetails', e.target.value)} />
      </label>
      <label className="bk-label">
        First aid given<Optional />
        <textarea className="bk-input" rows={2} value={form.firstAidGiven || ''} onChange={e => set('firstAidGiven', e.target.value)} />
      </label>
      <label className="bk-label">
        Outcome<Optional />
        <textarea className="bk-input" rows={2} value={form.outcome || ''} onChange={e => set('outcome', e.target.value)}
          placeholder="For example, sent home and advised to see their GP" />
      </label>
      <fieldset>
        <legend>Witness account</legend>
        <div className="bk-grid-2">
          <label className="bk-label">
            Witness name<Optional />
            <input type="text" className="bk-input" value={form.witnessName || ''} onChange={e => set('witnessName', e.target.value)} />
          </label>
          <label className="bk-label">
            Phone or email<Optional />
            <input type="text" className="bk-input" value={form.witnessContact || ''} onChange={e => set('witnessContact', e.target.value)} />
          </label>
        </div>
        <label className="bk-label">
          Witness statement<Optional />
          <textarea className="bk-input" rows={3} value={form.witnessStatement || ''} onChange={e => set('witnessStatement', e.target.value)} />
        </label>
      </fieldset>
    </>
  );
}

function check(form) {
  const errors = {};
  if (!form.incidentDate) errors.incidentDate = 'Enter the date and time';
  if (!form.description?.trim()) errors.description = 'Say what happened';
  return errors;
}

// A new first aid report (/new), or changing one (/:id/edit)
export default function AdminIncidentForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [gymnasts, setGymnasts] = useState([]);
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    Promise.all([
      bookingApi.getAllClubGymnasts(),
      isNew ? Promise.resolve({ data: EMPTY_FORM }) : bookingApi.getIncident(id),
    ]).then(([gRes, iRes]) => {
      setGymnasts(gRes.data);
      const start = { ...EMPTY_FORM, ...iRes.data };
      setLoaded(start);
      setForm(start);
    }).catch((err) => { console.error('Could not load the first aid report', err); setLoadError(true); });
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const changed = !!form && JSON.stringify(form) !== JSON.stringify(loaded);
  useUnsavedChanges(changed && !saving);

  const back = isNew
    ? { to: INCIDENTS_PATH, label: 'First aid reports' }
    : { to: `${INCIDENTS_PATH}/${id}`, label: 'First aid report' };

  const leave = (to) => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(to, { replace: true });
  };

  const cancel = async () => {
    if (changed && !(await confirm(DISCARD))) return;
    leave(back.to);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = check(form);
    setErrors(found);
    if (Object.keys(found).length) {
      setFormError('Fill in the highlighted fields.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (isNew) {
        await bookingApi.createIncident({
          ...form,
          incidentDate: new Date(form.incidentDate).toISOString(),
          gymnastId: form.gymnastId || undefined,
        });
        toast('First aid report saved');
        leave(INCIDENTS_PATH);
      } else {
        await bookingApi.updateIncident(id, {
          incidentDate: form.incidentDate,
          location: form.location,
          incidentType: form.incidentType,
          severity: form.severity,
          description: form.description,
          injuryDetails: form.injuryDetails,
          firstAidGiven: form.firstAidGiven,
          outcome: form.outcome,
          witnessName: form.witnessName,
          witnessContact: form.witnessContact,
          witnessStatement: form.witnessStatement,
        });
        toast('First aid report saved');
        leave(`${INCIDENTS_PATH}/${id}`);
      }
    } catch (err) {
      setFormError(err.response?.data?.error || "Couldn't save the report. Try again.");
      setSaving(false);
    }
  };

  const title = isNew ? 'New first aid report' : 'Edit first aid report';

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader back={back} title={title} />
      {loadError && <ErrorState title="Couldn't load the report" onRetry={load} />}
      {!loadError && !form && <Skeleton rows={5} label="Loading…" />}
      {form && (
        <form className="sg-form" onSubmit={handleSubmit} noValidate>
          <IncidentFormFields form={form} setForm={setForm} gymnasts={gymnasts} errors={errors} gymnastLocked={!isNew} />
          {formError && <p className="bk-form-error" role="alert">{formError}</p>}
          <div className="bk-actions">
            <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="bk-btn" disabled={saving} onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
